import { useRef, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { MoveHorizontal, MapPin } from "lucide-react";
import { PROJECTS } from "@/lib/siteData";

const Slider = ({ project }) => {
  const ref = useRef(null);
  const [pos, setPos] = useState(50);
  const [dragging, setDragging] = useState(false);

  const setFromClientX = useCallback((clientX) => {
    const rect = ref.current?.getBoundingClientRect();
    if (!rect) return;
    const pct = ((clientX - rect.left) / rect.width) * 100;
    setPos(Math.min(100, Math.max(0, pct)));
  }, []);

  const onDown = (e) => {
    setDragging(true);
    setFromClientX(e.clientX ?? e.touches?.[0]?.clientX);
  };
  const onMove = (e) => {
    if (!dragging) return;
    setFromClientX(e.clientX ?? e.touches?.[0]?.clientX);
  };
  const stop = () => setDragging(false);

  return (
    <div className="group">
      <div
        ref={ref}
        data-testid={`ba-slider-${project.id}`}
        onMouseDown={onDown}
        onMouseMove={onMove}
        onMouseUp={stop}
        onMouseLeave={stop}
        onTouchStart={onDown}
        onTouchMove={onMove}
        onTouchEnd={stop}
        className="relative aspect-[4/3] w-full overflow-hidden rounded-3xl ring-1 ring-black/10 select-none cursor-ew-resize"
      >
        {/* AFTER (base) */}
        <img src={project.after} alt={`${project.title} after`} draggable="false" className="absolute inset-0 h-full w-full object-cover" />
        <span className="absolute top-3 right-3 z-10 rounded-full bg-white/85 backdrop-blur px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-[#007AFF]">After</span>

        {/* BEFORE (clipped) */}
        <div className="absolute inset-0 overflow-hidden" style={{ width: `${pos}%` }}>
          <img
            src={project.before}
            alt={`${project.title} before`}
            draggable="false"
            className="absolute inset-0 h-full w-full object-cover"
            style={{ width: ref.current ? `${ref.current.offsetWidth}px` : "100%", maxWidth: "none" }}
          />
          <span className="absolute top-3 left-3 rounded-full bg-black/70 px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-white">Before</span>
        </div>

        {/* Handle */}
        <div className="absolute top-0 bottom-0 z-20 -ml-px" style={{ left: `${pos}%` }}>
          <div className="h-full w-0.5 bg-white shadow-[0_0_0_1px_rgba(0,0,0,0.1)]" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-11 w-11 rounded-full bg-white shadow-lg flex items-center justify-center ring-1 ring-black/10">
            <MoveHorizontal className="h-5 w-5 text-neutral-700" />
          </div>
        </div>
      </div>

      <div className="mt-5">
        <h3 className="text-xl md:text-2xl font-display font-bold tracking-tight">{project.title}</h3>
        <p className="mt-1.5 inline-flex items-center gap-1.5 text-sm text-neutral-500"><MapPin className="h-3.5 w-3.5" /> {project.location}</p>
        <p className="mt-2 text-[15px] leading-relaxed text-neutral-600">{project.blurb}</p>
      </div>
    </div>
  );
};

export const Gallery = () => {
  return (
    <section id="gallery" data-testid="gallery" className="py-20 md:py-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12 md:mb-16">
          <div className="max-w-2xl">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-neutral-400 mb-4">Recent work</p>
            <h2 className="font-display font-extrabold tracking-tight leading-none text-4xl sm:text-5xl lg:text-6xl">
              Before &amp; <span className="brand-gradient-text">after.</span>
            </h2>
          </div>
          <p className="text-base md:text-lg text-neutral-600 max-w-sm">
            Drag the slider on each project to see the transformation — from worn-out to done right.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-6">
          {PROJECTS.map((p, i) => (
            <motion.div
              key={p.id}
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.6, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] }}
            >
              <Slider project={p} />
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
